import React, { useState, useEffect } from 'react';
import { 
  MatrixCheckCircle, 
  MatrixXCircle, 
  MatrixDashboard, 
  MatrixClipboard, 
  MatrixUser,
  MatrixEye,
  MatrixRefresh,
  MatrixPlus
} from '../components/MatrixEmojis';
import { 
  signInWithPopup, 
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { auth } from '../config/firebase';
import { db } from '../config/firebase';

const AuthPrototypePage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userProfile, setUserProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  
  // Form states
  const [applicationForm, setApplicationForm] = useState({
    collegeName: '',
    country: '',
    course: 'MBBS',
    neetScore: '',
    category: '',
    documents: {
      marksheet10: null,
      marksheet12: null,
      passport: null,
      photo: null
    }
  });

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadUserData(currentUser.uid);
      } else {
        setUser(null);
        setUserProfile(null);
        setApplications([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadUserData = async (userId) => {
    try {
      // Load user profile
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        setUserProfile(userDoc.data());
      } else {
        // Create user profile if doesn't exist
        const newProfile = {
          uid: userId,
          email: auth.currentUser.email,
          name: auth.currentUser.displayName,
          photoURL: auth.currentUser.photoURL,
          createdAt: serverTimestamp(),
          profileComplete: false
        };
        await setDoc(doc(db, 'users', userId), newProfile);
        setUserProfile(newProfile);
      }

      // Load user applications
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', userId)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const userApplications = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setApplications(userApplications);
    } catch (error) {
      console.error('Error loading user data:', error);
      setError(`Failed to load user data: ${error.message}`);
    }
  };
  const handleGoogleLogin = async () => {
    setLoading('google');
    setError('');
    
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      console.log('Login successful:', result.user);
    } catch (error) {
      console.error('Login error:', error);
      setError(`Login failed: ${error.message}`);
    } finally {
      setLoading('');
    }  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setActiveTab('dashboard');
    } catch (error) {
      setError(`Logout failed: ${error.message}`);
    }
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('Please login to submit application');
      return;
    }

    setLoading('submit');
    try {
      const applicationData = {
        ...applicationForm,
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName,
        status: 'pending',
        submittedAt: serverTimestamp(),
        applicationId: `APP_${Date.now()}`
      };

      const docRef = await addDoc(collection(db, 'applications'), applicationData);
      
      // Reload applications
      await loadUserData(user.uid);
      
      // Reset form
      setApplicationForm({
        collegeName: '',
        country: '',
        course: 'MBBS',
        neetScore: '',
        category: '',
        documents: {
          marksheet10: null,
          marksheet12: null,
          passport: null,
          photo: null
        }
      });

      setActiveTab('applications');
      alert('Application submitted successfully!');
    } catch (error) {
      console.error('Error submitting application:', error);
      setError(`Submission failed: ${error.message}`);
    } finally {
      setLoading('');
    }
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await updateDoc(doc(db, 'applications', applicationId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      await loadUserData(user.uid);
    } catch (error) {
      setError(`Failed to update status: ${error.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'under_review': return 'text-blue-600 bg-blue-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <MatrixCheckCircle size="18" />;
      case 'rejected': return <MatrixXCircle size="18" />;
      case 'under_review': return <MatrixEye size="18" />;
      default: return <MatrixRefresh size="18" />;
    }
  };

  // Render login screen
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">            <h1 className="text-3xl font-bold text-gray-800 mb-6">
              � MBBS Admission Portal
            </h1>
            <p className="text-gray-600 mb-8">
              Sign in to manage your applications and track admission status
            </p>
            
            {error && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <strong>Error:</strong> {error}
              </div>
            )}
              <div className="space-y-4">
              <button
                onClick={handleGoogleLogin}
                disabled={loading === 'google'}
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200"
              >
                {loading === 'google' ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>                    Continue with Google
                  </>
                )}
              </button>
            </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Features:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Submit multiple college applications</li>
                <li>• Track application status in real-time</li>
                <li>• Secure document storage</li>
                <li>• Direct communication with admissions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main dashboard for authenticated users
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">� MBBS Portal</h1>
              <span className="text-sm text-gray-500">Welcome, {user.displayName}</span>
            </div>
            
            <div className="flex items-center space-x-4">
              {user.photoURL && (
                <img 
                  src={user.photoURL} 
                  alt="Profile" 
                  className="w-8 h-8 rounded-full"
                />
              )}
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">            {[
              { id: 'dashboard', label: <><MatrixDashboard size="18" /> Dashboard</>, count: null },
              { id: 'applications', label: <><MatrixClipboard size="18" /> Applications</>, count: applications.length },
              { id: 'new-application', label: <><MatrixPlus size="18" /> New Application</>, count: null },
              { id: 'profile', label: <><MatrixUser size="18" /> Profile</>, count: null }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count !== null && (
                  <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg mx-4">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Stats Cards */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <MatrixClipboard size="32" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Applications
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {applications.length}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <span className="text-2xl">⏱️</span>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Pending Review
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {applications.filter(app => app.status === 'pending').length}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <MatrixCheckCircle size="32" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Approved
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {applications.filter(app => app.status === 'approved').length}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Applications */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Recent Applications</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {applications.slice(0, 3).map((app) => (
                  <div key={app.id} className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{app.collegeName}</h4>
                      <p className="text-sm text-gray-500">{app.country} • {app.course}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                        {getStatusIcon(app.status)} {app.status}
                      </span>
                    </div>
                  </div>
                ))}
                {applications.length === 0 && (
                  <div className="px-6 py-8 text-center text-gray-500">
                    No applications yet. Start by creating your first application!
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">All Applications</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {applications.map((app) => (
                  <div key={app.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{app.collegeName}</h4>
                            <p className="text-sm text-gray-500">{app.country} • {app.course}</p>
                            <p className="text-xs text-gray-400">ID: {app.applicationId}</p>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                          <span>NEET Score: {app.neetScore}</span>
                          <span>Category: {app.category}</span>
                          <span>Submitted: {app.submittedAt?.toDate?.()?.toLocaleDateString() || 'Recently'}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(app.status)}`}>
                          {getStatusIcon(app.status)} {app.status}
                        </span>
                        
                        {/* Demo: Status change buttons (for testing) */}
                        <div className="flex space-x-1">
                          <button
                            onClick={() => updateApplicationStatus(app.id, 'under_review')}
                            className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          >
                            Review
                          </button>
                          <button
                            onClick={() => updateApplicationStatus(app.id, 'approved')}
                            className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => updateApplicationStatus(app.id, 'rejected')}
                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {applications.length === 0 && (
                  <div className="px-6 py-8 text-center text-gray-500">
                    <p>No applications found.</p>
                    <button
                      onClick={() => setActiveTab('new-application')}
                      className="mt-2 text-indigo-600 hover:text-indigo-500"
                    >
                      Create your first application →
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* New Application Tab */}
        {activeTab === 'new-application' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">New Application</h3>
                <p className="text-sm text-gray-500">Submit a new college application</p>
              </div>
              <form onSubmit={handleApplicationSubmit} className="px-6 py-4 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      College/University Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={applicationForm.collegeName}
                      onChange={(e) => setApplicationForm({...applicationForm, collegeName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., AIIMS Delhi, CMC Vellore"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country *
                    </label>
                    <select
                      required
                      value={applicationForm.country}
                      onChange={(e) => setApplicationForm({...applicationForm, country: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Country</option>
                      <option value="India">India</option>
                      <option value="Russia">Russia</option>
                      <option value="Ukraine">Ukraine</option>
                      <option value="Philippines">Philippines</option>
                      <option value="Nepal">Nepal</option>
                      <option value="Bangladesh">Bangladesh</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Course *
                    </label>
                    <select
                      required
                      value={applicationForm.course}
                      onChange={(e) => setApplicationForm({...applicationForm, course: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="MBBS">MBBS</option>
                      <option value="BDS">BDS</option>
                      <option value="BAMS">BAMS</option>
                      <option value="BHMS">BHMS</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      NEET Score *
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      max="720"
                      value={applicationForm.neetScore}
                      onChange={(e) => setApplicationForm({...applicationForm, neetScore: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter NEET score"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      required
                      value={applicationForm.category}
                      onChange={(e) => setApplicationForm({...applicationForm, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Category</option>
                      <option value="General">General</option>
                      <option value="OBC">OBC</option>
                      <option value="SC">SC</option>
                      <option value="ST">ST</option>
                      <option value="EWS">EWS</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setActiveTab('dashboard')}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading === 'submit'}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loading === 'submit' ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Profile Information</h3>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center space-x-6 mb-6">
                  {user.photoURL && (
                    <img 
                      src={user.photoURL} 
                      alt="Profile" 
                      className="w-20 h-20 rounded-full"
                    />
                  )}
                  <div>
                    <h4 className="text-xl font-medium text-gray-900">{user.displayName}</h4>
                    <p className="text-gray-500">{user.email}</p>
                    <p className="text-sm text-gray-400">UID: {user.uid}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Email Verified:</span>
                    <span className={`ml-2 ${user.emailVerified ? 'text-green-600' : 'text-red-600'}`}>
                      {user.emailVerified ? <><MatrixCheckCircle size="16" /> Yes</> : <><MatrixXCircle size="16" /> No</>}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Account Created:</span>
                    <span className="ml-2 text-gray-600">
                      {new Date(user.metadata.creationTime).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Last Sign In:</span>
                    <span className="ml-2 text-gray-600">
                      {new Date(user.metadata.lastSignInTime).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Total Applications:</span>
                    <span className="ml-2 text-gray-600">{applications.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AuthPrototypePage;
